import { useEffect, useMemo, useRef, useState } from 'react';

import { CheckIcon, ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@/assets/icons';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SvgIcon,
} from '@/components/common';
import { mergeClasses } from '@/utils';

import { IDropdownSearch } from './DropdownSearch.interfaces';
import { renderLabelStyle } from './DropdownSearch.utils';
import { useDebounce } from '@/hooks';

const DropdownSearch = ({
  className,
  options,
  onSelect,
  minWidth,
  maxWidth,
  width,
  label,
  error,
  required,
  searchable = true,
  emptyIndicator,
  popoverClassName,
  defaultLabel,
  disabled = false,
  placeholder = 'Search',
  searchCallback,
  isModal = false,
}: IDropdownSearch) => {
  const [selectedLabel, setSelectedLabel] = useState<string | undefined>(defaultLabel);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [optionsState, setOptionsState] = useState(options || []);
  const [searchValue, setSearchValue] = useState('');
  const [searchCursor, setSearchCursor] = useState<string | undefined>(undefined);
  const [isEndReached, setEndReached] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const debouncedSearchValue = useDebounce(searchValue);

  const clearAllSelected = () => {
    if (disabled) return;
    setSelectedLabel('');
    onSelect?.('');
  };

  useEffect(() => {
    const fetchOptions = async () => {
      if (!searchCallback) return;

      try {
        const serverOptions = await searchCallback({ searchBy: debouncedSearchValue });
        setOptionsState(serverOptions.data);
        setSearchCursor(serverOptions.cursor);
        setEndReached(false);
      } catch (error) {
        console.error('Failed to fetch options:', error);
      }
    };

    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue]);

  const filteredOptions = useMemo(() => {
    // If options are provided via props, filter them locally
    if (options)
      return options.filter((option) =>
        option.label.toLowerCase().includes(debouncedSearchValue.toLowerCase()),
      );
    return optionsState;
  }, [options, debouncedSearchValue, optionsState]);

  const loadMoreOptions = async () => {
    if (!searchCallback || isLoadingMore || isEndReached || !searchCursor) return;

    setIsLoadingMore(true);
    try {
      const serverOptions = await searchCallback({
        searchBy: debouncedSearchValue,
        cursor: searchCursor,
      });

      // Check if cursor is the same (end reached)
      if (serverOptions.cursor === searchCursor) {
        setEndReached(true);
      } else {
        setOptionsState((prev) => [...prev, ...serverOptions.data]);
        setSearchCursor(serverOptions.cursor);
      }
    } catch (error) {
      console.error('Failed to load more options:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;

    // Check if scrolled to bottom (with small threshold)
    if (scrollHeight - scrollTop <= clientHeight + 10) {
      loadMoreOptions();
    }
  };

  useEffect(() => {
    if (!isPopoverOpen) {
      setSearchValue('');
      setSearchCursor(undefined);
      setEndReached(false);
    }
  }, [isPopoverOpen]);

  return (
    <div className={mergeClasses('flex flex-col gap-1.5 border-neutral-n4 w-full', className)}>
      {label && (
        <label className="text-sm font-semibold text-neutral-n9">
          {label}
          {required && <span className="ml-1 text-semantic-error-base">*</span>}
        </label>
      )}
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={isModal}>
        <PopoverTrigger disabled={disabled} asChild>
          <Button
            ref={buttonRef}
            variant="cancel"
            role="combobox"
            aria-expanded={isPopoverOpen}
            className={mergeClasses(
              'flex rounded-lg border min-h-10 h-auto items-center justify-between bg-white hover:shadow-none active:shadow-none data-[state=open]:!border-brand-primary-base [&_svg]:pointer-events-auto !text-sm !font-medium text-brand-primary-darkest px-4 !shadow-none break-all',
              disabled &&
                '!bg-neutral-n2 hover:!border-neutral-n4 [&_*]:!text-neutral-n6 cursor-not-allowed',
              error ? '!border-semantic-error-base' : '!border-neutral-n4 hover:!border-neutral-n6',
              className,
            )}
            style={{
              minWidth: minWidth || '200px',
              maxWidth: maxWidth || '100%',
              width: width || '100%',
            }}
          >
            {selectedLabel ? (
              <>
                <span className="flex-1 text-left">
                  {selectedLabel ? renderLabelStyle(selectedLabel) : placeholder}
                </span>
                <div className="flex items-center justify-between gap-3">
                  <CloseIcon
                    className="w-4 h-4 cursor-pointer text-neutral-n6 hover:text-brand-primary-darkest"
                    onClick={(event) => {
                      event.stopPropagation();
                      clearAllSelected();
                    }}
                  />
                  <SvgIcon
                    className={mergeClasses(
                      'w-4 h-4 cursor-pointer',
                      isPopoverOpen ? 'text-brand-primary-base' : 'text-brand-primary-darkest',
                    )}
                    component={isPopoverOpen ? ChevronUpIcon : ChevronDownIcon}
                  />
                </div>
              </>
            ) : (
              <div className="flex items-center justify-between w-full mx-auto">
                <span className="text-sm text-muted-foreground">{placeholder}</span>
                <SvgIcon
                  className={mergeClasses(
                    'w-4 h-4 cursor-pointer',
                    isPopoverOpen ? 'text-brand-primary-base' : 'text-brand-primary-darkest',
                  )}
                  component={isPopoverOpen ? ChevronUpIcon : ChevronDownIcon}
                />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={mergeClasses(
            'w-auto min-w-[300px] shadow-none border border-solid border-neutral-n4',
            popoverClassName,
          )}
          style={{
            minWidth: minWidth || '200px',
            maxWidth: maxWidth || '100%',
            width: buttonRef.current?.offsetWidth || width,
            maxHeight: '60vh',
          }}
        >
          <Command className="rounded-soft">
            {searchable && (
              <div className="border-b border-neutral-n4">
                <CommandInput
                  hasSearchIcon
                  placeholder="Search"
                  value={searchValue}
                  onValueChange={setSearchValue}
                  className="w-full"
                />
              </div>
            )}
            <CommandList
              onScroll={onScroll}
              className="max-h-[240px] overflow-y-auto multiselect-scrollbar overscroll-behavior-y-contain [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            >
              <CommandEmpty>{emptyIndicator || 'No results found.'}</CommandEmpty>{' '}
              <CommandGroup className="!p-0">
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    keywords={[option.label]}
                    value={option.value}
                    onSelect={() => {
                      setSelectedLabel(option.label);
                      onSelect?.(option.value);
                      setIsPopoverOpen(false);
                    }}
                    className="min-h-10 break-all"
                  >
                    <span className="flex-1">{renderLabelStyle(option.label)}</span>
                    <CheckIcon
                      className={mergeClasses(
                        'ml-auto',
                        selectedLabel === option.label ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && <p className="text-xs text-semantic-error-base">{error}</p>}
    </div>
  );
};

export default DropdownSearch;
